import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { MdCheckCircle, MdError, MdStorefront, MdCardGiftcard } from "react-icons/md";
import { API_URL } from "../../constant";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "./ClaimDistribution.css";

const ClaimDistribution = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const tokenParam = searchParams.get("token") || "";

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(null);
  const [error, setError] = useState(null);

  const authToken = localStorage.getItem("auth_token");
  const isAuthenticated = Boolean(authToken);

  // Fetch public preview of distribution
  useEffect(() => {
    if (!tokenParam) {
      setError("No transaction distribution token provided.");
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/api/public/tc-distribution/details/${encodeURIComponent(tokenParam)}`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        );

        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || "Failed to load distribution details.");
        }

        setDetails(data.data);
      } catch (err) {
        console.error("Fetch distribution details error:", err);
        setError(err.message || "Unable to find this transaction distribution.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [tokenParam]);

  // Handle claim action
  const handleClaim = async () => {
    if (!isAuthenticated) {
      // Save current page URL so after login customer comes back
      const returnUrl = `/customer/claim-distribution${location.search}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    if (!tokenParam) return;

    setClaiming(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/customer/tc-distribution/claim`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ token: tokenParam }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to claim TopCoin reward.");
      }

      setClaimSuccess(data.data);
      toast.success(data.message || "Transaction completed! TopCoins credited.");
    } catch (err) {
      console.error("Claim error:", err);
      setError(err.message || "Failed to complete transaction.");
      toast.error(err.message || "Failed to complete transaction.");
    } finally {
      setClaiming(false);
    }
  };

  const isCustomerPortal = location.pathname.startsWith("/customer");

  return (
    <>
      {!isCustomerPortal && <Header />}
      <div className="claim-dist-container">
        <div className="claim-dist-card">
          <div className="claim-dist-header">
            <h2>TopCoin UK</h2>
            <p>Retail Purchase Reward Distribution</p>
          </div>

          <div className="claim-dist-body">
            {loading && (
              <p style={{ padding: "40px 0", color: "#6b7280" }}>
                Loading transaction details...
              </p>
            )}

            {!loading && error && !claimSuccess && (
              <div style={{ padding: "20px 0" }}>
                <MdError size={48} style={{ color: "#ef4444", marginBottom: 12 }} />
                <h3 style={{ color: "#b91c1c", margin: "0 0 8px" }}>Transaction Notice</h3>
                <p style={{ color: "#4b5563", fontSize: 14 }}>{error}</p>
                <Link to="/customer/wallet" className="claim-dist-secondary-btn">
                  Back to My Wallet
                </Link>
              </div>
            )}

            {!loading && !error && !claimSuccess && details && (
              <>
                <div className="claim-dist-badge">
                  <MdStorefront /> {details.shop_name || "TopCoin Retail Partner"}
                </div>

                <div className="claim-dist-reward-box">
                  <h3>
                    +{details.awarding_topcoin} TC
                    {details.awarding_mtopcoin > 0 ? ` & ${details.awarding_mtopcoin} mTC` : ""}
                  </h3>
                  <p>Ready to be credited to your TopCoin Wallet</p>
                </div>

                <div className="claim-dist-info-list">
                  <div className="claim-dist-info-row">
                    <span>Transaction Total:</span>
                    <strong>£{Number(details.transaction_total).toFixed(2)}</strong>
                  </div>
                  <div className="claim-dist-info-row">
                    <span>Invoice Number:</span>
                    <strong>{details.invoice_no}</strong>
                  </div>
                  <div className="claim-dist-info-row">
                    <span>Cashier / Staff:</span>
                    <strong>{details.employee_name} ({details.employee_no})</strong>
                  </div>
                  <div className="claim-dist-info-row">
                    <span>Transaction Type:</span>
                    <strong>{details.transaction_type}</strong>
                  </div>
                </div>

                {details.status === "completed" ? (
                  <div style={{ color: "#15803d", fontWeight: 700, padding: "10px 0" }}>
                    This transaction has already been completed.
                  </div>
                ) : details.is_expired ? (
                  <div style={{ color: "#dc2626", fontWeight: 700, padding: "10px 0" }}>
                    This transaction QR code has expired.
                  </div>
                ) : isAuthenticated ? (
                  <button
                    type="button"
                    className="claim-dist-btn"
                    disabled={claiming}
                    onClick={handleClaim}
                  >
                    <MdCardGiftcard style={{ verticalAlign: "middle", marginRight: 8 }} />
                    {claiming ? "Processing Transaction..." : "Claim My TopCoins Now"}
                  </button>
                ) : (
                  <Link
                    to={`/login?returnUrl=${encodeURIComponent(`/customer/claim-distribution${location.search}`)}`}
                    className="claim-dist-login-btn"
                  >
                    Log In to Claim Reward
                  </Link>
                )}
              </>
            )}

            {claimSuccess && (
              <div style={{ padding: "10px 0" }}>
                <MdCheckCircle className="claim-dist-success-icon" />
                <h3 style={{ color: "#15803d", margin: "0 0 6px" }}>Reward Claimed!</h3>
                <p style={{ color: "#4b5563", fontSize: 14, margin: "0 0 18px" }}>
                  Your TopCoins have been safely credited to your wallet.
                </p>

                <div className="claim-dist-info-list">
                  <div className="claim-dist-info-row">
                    <span>SBC Reference:</span>
                    <strong>{claimSuccess.sbc_ref}</strong>
                  </div>
                  <div className="claim-dist-info-row">
                    <span>TopCoins Awarded:</span>
                    <strong style={{ color: "#16a34a" }}>
                      +{claimSuccess.awarding_topcoin} TopCoin
                      {claimSuccess.awarding_mtopcoin > 0 ? ` / ${claimSuccess.awarding_mtopcoin} mTC` : ""}
                    </strong>
                  </div>
                  <div className="claim-dist-info-row">
                    <span>Store:</span>
                    <strong>{claimSuccess.retail_business?.name || "Retail Store"}</strong>
                  </div>
                </div>

                <Link to="/customer/wallet" className="claim-dist-btn" style={{ display: "inline-block", textDecoration: "none" }}>
                  View My Wallet
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isCustomerPortal && <Footer />}
    </>
  );
};

export default ClaimDistribution;
